import React, { useEffect, useRef, useState } from "react";

type Props = {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  volume: number;
  onVolumeChange: (v: number) => void;
  fastForwardSeconds?: number;
};

const fmt = (seconds: number) => {
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
};

const AudioDevPanel: React.FC<Props> = ({
  audioRef,
  volume,
  onVolumeChange,
  fastForwardSeconds = 10,
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const tick = () => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
        setDuration(audioRef.current.duration || 0);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [audioRef]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseFloat(e.target.value);
    onVolumeChange(v);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const handleFastForward = () => {
    if (!audioRef.current) return;
    const dur = audioRef.current.duration;
    const newTime = audioRef.current.currentTime + fastForwardSeconds;
    audioRef.current.currentTime =
      isFinite(dur) && dur > 0 ? Math.min(newTime, dur) : newTime;
  };

  const panelStyle: React.CSSProperties = {
    position: "fixed",
    top: 12,
    right: 12,
    zIndex: 9999,
    background: "rgba(0,0,0,0.75)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.25)",
    borderRadius: 10,
    padding: "10px 14px",
    fontFamily: "monospace",
    fontSize: 13,
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 210,
    userSelect: "none",
  };

  const rowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
  };

  const labelStyle: React.CSSProperties = {
    width: 60,
    opacity: 0.7,
    flexShrink: 0,
  };

  const btnStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.15)",
    color: "#fff",
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 6,
    padding: "3px 10px",
    cursor: "pointer",
    fontSize: 13,
  };

  return (
    <div style={panelStyle}>
      <div style={{ opacity: 0.5, fontSize: 11, letterSpacing: 1 }}>
        ▶ BGM DEV
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Time</span>
        <span>
          {fmt(currentTime)} / {duration > 0 ? fmt(duration) : "--:--"}
        </span>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Volume</span>
        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={volume}
          onChange={handleVolumeChange}
          style={{ flex: 1, cursor: "pointer" }}
        />
        <span style={{ width: 34, textAlign: "right" }}>
          {volume.toFixed(2)}
        </span>
      </div>

      <div style={rowStyle}>
        <span style={labelStyle}>Skip</span>
        <button style={btnStyle} onClick={handleFastForward}>
          +{fastForwardSeconds}s ⏩
        </button>
      </div>
    </div>
  );
};

export default AudioDevPanel;
